import {useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912) — nulls-last, numeric-vs-string and the
// empty-string rule live in one place rather than being re-derived per table.
import {sortRecords} from '@flexinet/ui-components';

import {
    useDnsRecordsFilter,
    useDnsRecordsInfiniteQuery,
} from './useDnsRecordsQuery';
import {useDnsRecordsSearch} from './useDnsRecordsSearch';
import {DnsRecord} from '../types';

// The DEFAULT sort is a per-entity judgement and stays local; HOW two values
// compare does not (that is `sortRecords`).
//
// `name` rather than a timestamp: a `DnsRecord` as this neutral layer returns
// it has NO created/updated field at all — it is fetched from the provider on
// every read, not stored here, so there is no such thing as "newest first". Of
// what remains, `name` is the only key that is both non-null (`String!`) and
// stable. Alphabetical is also how an operator scans a zone file.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<DnsRecord> = {
    columnAccessor: 'name',
    direction: 'asc',
};

// ⚠️ THE KEYWORD SEARCHES THE VALUES TOO, AND THAT INCLUDES POINTER TARGETS.
//
// `values` holds the resolved target for a pointer record, so searching it
// means an operator can find "which record points at this load balancer" —
// which is one of the few questions worth asking of a zone and is unanswerable
// at the provider's own console without clicking every alias in turn.
export const matchesKeyword = (item: DnsRecord, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.name,
        item.type,
        ...(item.values ?? []),
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

export const useDnsRecords = (connectionId?: string, zoneId?: string) => {

    // ⚠️ THE FILTER GOES INTO THE QUERY, not into a client-side pass. It is a
    // server-side argument, so changing the type re-fetches from page one
    // against a different result set rather than re-narrowing loaded rows.
    const {filters} = useDnsRecordsFilter();

    const query = useDnsRecordsInfiniteQuery(
        connectionId,
        zoneId,
        filters.type ? {type: filters.type} : undefined,
    );

    // Component state, not the module-scoped store: sort order is how ONE
    // operator is looking at the table right now, unlike the keyword and the
    // filters, which are shared across sibling components.
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<DnsRecord>>(DEFAULT_SORT_STATUS);

    const {keyword} = useDnsRecordsSearch();

    const pages = query.data?.pages ?? [];

    // The `?? []` is not defensive noise — a page object that came back partial
    // would otherwise make this flatMap throw inside a render.
    const items: DnsRecord[] = pages.flatMap(
        page => (page && 'items' in page) ? (page.items ?? []) : [],
    ).filter(Boolean);

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ SORTING AND THE KEYWORD ARE CLIENT-SIDE, over the pages fetched so
    // far. Neither can reach rows the browser has not loaded. That is a
    // property of token pagination, not an oversight — and it is why the footer
    // states a LOADED count rather than a total. The TYPE FILTER is different:
    // it is server-side, so it narrows the whole dataset rather than the window.
    const visibleItems = sortRecords(
        items.filter(item => matchesKeyword(item, needle)),
        sortStatus,
    );

    return {
        // The loaded set BEFORE the keyword narrows it — the window the
        // footer's count is measured against.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        // ⚠️ DERIVED FROM hasNextPage, NEVER FROM items.length. With a
        // server-side filter an empty page with more behind it is the common
        // case, not an edge one.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
        isReloading: query.isRefetching,
    };
};
